import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * <subscription-bundle-builder>
 * Custom bundle builder that mirrors the Recharge Bundles 2.0 line-item-property
 * contract (`_rc_bundle`, `_rc_bundle_parent`, `_rc_bundle_variant`) so the
 * existing cart/checkout/Recharge integration continues to work unchanged.
 *
 * Bundle / discount rules are configured in two places (manual mirror):
 *   1. Recharge admin (selling plan + discount + min/max qty)
 *   2. Section settings (min/max + bundle parent product)
 */
class SubscriptionBundleBuilder extends Component {
  requiredRefs = ['tray', 'slots', 'subtotal', 'cta'];

  state = {
    items: [],
    upsells: [],
    frequencyName: '',
    minItems: 6,
    maxItems: 6,
    parentHandle: '',
    parentVariantId: '',
    presetTitle: '',
    presetHandle: '',
    presetImage: '',
    presetDescription: '',
    purchaseOptions: 'subscription_only',
    purchaseType: 'subscribe',
    requirePlans: true,
    discountTiers: [],
    freeShippingThreshold: 0,
    gift: null,
    submitting: false,
  };

  connectedCallback() {
    super.connectedCallback();

    this.state.minItems = parseInt(this.dataset.minItems, 10) || 6;
    this.state.maxItems = parseInt(this.dataset.maxItems, 10) || this.state.minItems;
    // 'add_to_cart' (open cart drawer) or 'checkout' (go straight to checkout).
    this.state.ctaAction = this.dataset.ctaAction === 'checkout' ? 'checkout' : 'add_to_cart';
    this.state.parentHandle = this.dataset.parentHandle || '';
    this.state.parentVariantId = this.dataset.parentVariantId || '';
    this.state.purchaseOptions = this.dataset.purchaseOptions || 'subscription_only';
    this.state.purchaseType = this.dataset.defaultPurchaseType || 'subscribe';
    this.dataset.purchaseType = this.state.purchaseType;
    // When subscription isn't available (one-time Flex Bundle), products don't need
    // a selling plan and the bundle price tallies up from the selected variants.
    this.state.requirePlans = this.dataset.requirePlans === 'true';
    // Tiered one-time discount: [{qty, percent}], sorted ascending by qty so the
    // highest qualifying tier wins and the next tier is easy to find.
    try {
      const tiers = JSON.parse(this.dataset.discountTiers || '[]');
      this.state.discountTiers = (Array.isArray(tiers) ? tiers : [])
        .map((t) => ({ qty: parseInt(t.qty, 10) || 0, percent: parseFloat(t.percent) || 0 }))
        .filter((t) => t.qty > 0 && t.percent > 0)
        .sort((a, b) => a.qty - b.qty);
    } catch (_) {
      this.state.discountTiers = [];
    }
    this.tierProgressLabel = this.dataset.tierProgressLabel || 'Add [count] more to save [percent]%';
    this.tierUnlockedLabel = this.dataset.tierUnlockedLabel || "You're saving [percent]%";
    this.tierUnlockLabel = this.dataset.tierUnlockLabel || '🎉 [percent]% off unlocked!';
    this.state.freeShippingThreshold = parseInt(this.dataset.freeShippingThreshold, 10) || 0;
    this.freeShippingProgressLabel = this.dataset.freeShippingProgressLabel || "You're [amount] away from free shipping";
    this.freeShippingUnlockedLabel = this.dataset.freeShippingUnlockedLabel || "You've unlocked free shipping!";

    if (this.dataset.giftEnabled === 'true') {
      this.state.gift = {
        variantId: this.dataset.giftVariantId,
        handle: this.dataset.giftHandle,
        title: this.dataset.giftTitle,
        image: this.dataset.giftImage,
        price: parseInt(this.dataset.giftPrice, 10) || 0,
      };
    }

    const initial = this.querySelector('.bundle-builder__frequency-option--active input');
    this.state.frequencyName = initial?.value || '';

    // Modal context (Quick Add): the builder is a compact instance inside the
    // Quick Add modal. The host-page URL isn't this product's, so ignore URL
    // presets, and don't touch the PDP's saved draft (shared storage key) —
    // instead open pre-filled with a deterministic default set.
    this.modalContext = this.dataset.context === 'quick-add';

    if (this.modalContext) {
      this.#applyAutoDefault();
    } else {
      // A ?preset=<handle> deep link from a set tile drives an ephemeral, URL-bound
      // bundle: it's applied fresh from the param every load and is NOT persisted
      // while pristine (see #persist). The param stays in the URL until the shopper
      // edits the selection, at which point it becomes a normal saved draft.
      // Without a param, restore any previously-saved (edited) draft.
      const prefilled = this.#applyPresetFromUrl();
      if (!prefilled) this.#restore();
    }

    this.#buildTabs();
    this.#observeTrayHeight();
    this.dataset.trayActive = 'false';
    this.#observeTrayReveal();
    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.scrollspy?.disconnect();
    this.trayResize?.disconnect();
    this.trayReveal?.disconnect();
    clearTimeout(this.unlockTimer);
  }

  // Keep the grid's bottom padding in sync with the fixed mobile tray so the last
  // row of products is never hidden behind it. (Desktop zeroes this padding.)
  #observeTrayHeight() {
    const tray = this.refs.tray;
    if (!tray || typeof ResizeObserver === 'undefined') return;
    // Only write when the height actually changes. On mobile the tray's
    // dvh-based max-height jitters by a pixel as the URL bar shows/hides on
    // scroll; without this guard every scroll frame rewrote the CSS var, which
    // reflowed the page (and re-fired the reveal observer) - freezing scroll.
    let lastHeight = -1;
    this.trayResize = new ResizeObserver(() => {
      const height = Math.round(tray.offsetHeight);
      if (height === lastHeight) return;
      lastHeight = height;
      this.style.setProperty('--bb-tray-height', `${height}px`);
    });
    this.trayResize.observe(tray);
  }

  // Reveal the fixed mobile tray only once the builder itself is scrolled into
  // view - it shouldn't sit at the bottom of the screen while the shopper is
  // still above the bundle builder. CSS keys off data-tray-active.
  #observeTrayReveal() {
    if (typeof IntersectionObserver === 'undefined') {
      this.dataset.trayActive = 'true';
      return;
    }
    this.trayReveal = new IntersectionObserver(
      (entries) => {
        const next = entries.some((e) => e.isIntersecting) ? 'true' : 'false';
        // Only write on an actual state change. This observer watches the root,
        // whose padding tracks --bb-tray-height, so it can re-fire on scroll;
        // rewriting the attribute each time forced a style recalc against the
        // body:has() selectors and froze scrolling.
        if (this.dataset.trayActive !== next) this.dataset.trayActive = next;
      },
      { rootMargin: '0px 0px -25% 0px', threshold: 0 }
    );
    this.trayReveal.observe(this);
  }

  #buildTabs() {
    const tabs = this.refs.tabs;
    const panels = this.querySelectorAll('.bundle-builder__panel');
    if (!tabs || panels.length === 0) return;

    tabs.innerHTML = '';
    panels.forEach((panel, idx) => {
      const id = panel.dataset.panelId;
      const label = panel.dataset.tabLabel || `Tab ${idx + 1}`;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bundle-builder__tab' + (idx === 0 ? ' bundle-builder__tab--active' : '');
      button.dataset.tabTarget = id;
      button.setAttribute('on:click', '/handleTabClick');
      button.textContent = label;
      tabs.appendChild(button);
    });

    this.#setupScrollspy(panels);
  }

  #setupScrollspy(panels) {
    if (typeof IntersectionObserver === 'undefined') return;
    if (this.scrollspy) this.scrollspy.disconnect();

    // In the Quick Add modal the builder scrolls inside the modal content, not
    // the viewport — measure panel intersection against that container so the
    // active tab tracks the modal's scroll rather than the page's.
    const root = this.modalContext ? this.closest('.quick-add-modal__content') : null;

    this.scrollspy = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        const id = visible.target.dataset.panelId;
        this.querySelectorAll('.bundle-builder__tab').forEach((t) => {
          t.classList.toggle('bundle-builder__tab--active', t.dataset.tabTarget === id);
        });
      },
      { root, rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5] }
    );

    panels.forEach((p) => this.scrollspy.observe(p));
  }

  // -- handlers -----------------------------------------------------------

  handleTabClick(event) {
    const button = event.target.closest('[data-tab-target]');
    if (!button) return;
    const targetId = button.dataset.tabTarget;
    const panel = this.querySelector(`[data-panel-id="${targetId}"]`);
    if (!panel) return;

    this.querySelectorAll('.bundle-builder__tab').forEach((t) => {
      t.classList.toggle('bundle-builder__tab--active', t.dataset.tabTarget === targetId);
    });

    panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  handleTrayToggle() {
    const tray = this.refs.tray;
    const expanded = tray.dataset.state === 'expanded';
    tray.dataset.state = expanded ? 'collapsed' : 'expanded';
    this.refs.trayToggle?.setAttribute('aria-expanded', expanded ? 'false' : 'true');
  }

  handlePurchaseTypeChange(event) {
    const input = event.target;
    if (!input || input.type !== 'radio') return;
    this.state.purchaseType = input.value;
    this.dataset.purchaseType = input.value;

    this.querySelectorAll('.bundle-builder__purchase-option').forEach((opt) => {
      opt.classList.toggle('bundle-builder__purchase-option--active', opt.contains(input));
    });

    const fieldset = this.refs.frequencyFieldset;
    if (fieldset) {
      if (this.state.purchaseType === 'subscribe') fieldset.removeAttribute('hidden');
      else fieldset.setAttribute('hidden', '');
    }

    const showGift = this.state.purchaseType === 'subscribe' && !!this.state.gift;
    if (this.refs.giftTrayCard) {
      if (showGift) this.refs.giftTrayCard.removeAttribute('hidden');
      else this.refs.giftTrayCard.setAttribute('hidden', '');
    }

    // Re-baseline the unlock celebration so toggling one-time/subscribe doesn't burst.
    this.lastReachedTierQty = undefined;
    this.render();
  }

  handleFrequencyChange(event) {
    const input = event.target;
    if (!input || input.type !== 'radio') return;
    this.state.frequencyName = input.value;

    this.querySelectorAll('.bundle-builder__frequency-option').forEach((opt) => {
      opt.classList.toggle('bundle-builder__frequency-option--active', opt.contains(input));
    });

    this.render();
  }

  handleTileHover(event) {
    const tile = event.target.closest('.bundle-builder__tile');
    const video = tile?.querySelector('.bundle-builder__tile-video');
    if (video && typeof video.play === 'function') {
      video.play().catch(() => {});
    }
  }

  handleTileUnhover(event) {
    const tile = event.target.closest('.bundle-builder__tile');
    const video = tile?.querySelector('.bundle-builder__tile-video');
    if (video) video.pause();
  }

  handleTileAdd(event) {
    const tile = event.target.closest('.bundle-builder__tile');
    if (!tile || tile.dataset.disabled === 'true') return;

    const productId = tile.dataset.productId;
    const existing = this.state.items.find((i) => i.tileKey === productId);
    if (existing) return;

    if (this.#totalQuantity() >= this.state.maxItems) return;

    let plans = [];
    try {
      plans = JSON.parse(tile.dataset.sellingPlans || '[]');
    } catch (err) {
      console.error('[bundle-builder] Could not parse selling plans for', tile.dataset.productHandle, err, tile.dataset.sellingPlans);
      return;
    }
    // A selling plan is only mandatory in subscription mode. One-time Flex Bundles
    // add plan-less products at their variant price.
    if (this.state.requirePlans && plans.length === 0) {
      console.warn('[bundle-builder] No selling plans on', tile.dataset.productHandle);
      return;
    }

    this.state.items.push({
      tileKey: productId,
      productHandle: tile.dataset.productHandle,
      productTitle: tile.dataset.productTitle,
      variantId: tile.dataset.variantId,
      variantPrice: parseInt(tile.dataset.variantPrice, 10) || 0,
      image: tile.dataset.image,
      sellingPlans: plans,
      quantity: 1,
    });

    this.#clearPresetAttribution();

    this.#track('bundle_item_added', {
      parent: this.state.parentHandle,
      variantId: tile.dataset.variantId,
      title: tile.dataset.productTitle,
      size: this.#totalQuantity(),
    });

    this.render();
    this.#scrollLatestSlotIntoView();
  }

  handleQtyIncrement(event) {
    const tile = event.target.closest('.bundle-builder__tile');
    if (!tile) return;
    const item = this.state.items.find((i) => i.tileKey === tile.dataset.productId);
    if (!item) return;

    if (this.#totalQuantity() >= this.state.maxItems) return;
    item.quantity += 1;
    this.#clearPresetAttribution();
    this.render();
    this.#scrollLatestSlotIntoView();
  }

  handleQtyDecrement(event) {
    const tile = event.target.closest('.bundle-builder__tile');
    if (!tile) return;
    const idx = this.state.items.findIndex((i) => i.tileKey === tile.dataset.productId);
    if (idx === -1) return;

    this.state.items[idx].quantity -= 1;
    if (this.state.items[idx].quantity <= 0) {
      this.state.items.splice(idx, 1);
    }
    this.#clearPresetAttribution();
    this.render();
  }

  handleUpsellAdd(event) {
    const card = event.target.closest('.bundle-builder__upsell-card');
    if (!card) return;
    if (this.#totalUpsellQuantity() >= this.#upsellMax()) return;
    const key = card.dataset.upsellKey;
    if (this.state.upsells.find((u) => u.tileKey === key)) return;

    this.state.upsells.push({
      tileKey: key,
      variantId: card.dataset.variantId,
      productTitle: card.dataset.productTitle,
      price: parseInt(card.dataset.variantPrice, 10) || 0,
      image: card.dataset.image,
      quantity: 1,
    });

    this.render();
  }

  handleUpsellQtyIncrement(event) {
    const card = event.target.closest('.bundle-builder__upsell-card');
    if (!card) return;
    if (this.#totalUpsellQuantity() >= this.#upsellMax()) return;
    const item = this.state.upsells.find((u) => u.tileKey === card.dataset.upsellKey);
    if (!item) return;
    item.quantity += 1;
    this.render();
  }

  handleUpsellQtyDecrement(event) {
    const card = event.target.closest('.bundle-builder__upsell-card');
    if (!card) return;
    const idx = this.state.upsells.findIndex((u) => u.tileKey === card.dataset.upsellKey);
    if (idx === -1) return;
    this.state.upsells[idx].quantity -= 1;
    if (this.state.upsells[idx].quantity <= 0) {
      this.state.upsells.splice(idx, 1);
    }
    this.render();
  }

  handleUpsellSlotRemove(event) {
    const slot = event.target.closest('[data-upsell-key]');
    if (!slot) return;
    const key = slot.dataset.upsellKey;
    const idx = this.state.upsells.findIndex((u) => u.tileKey === key);
    if (idx === -1) return;
    this.state.upsells[idx].quantity -= 1;
    if (this.state.upsells[idx].quantity <= 0) {
      this.state.upsells.splice(idx, 1);
    }
    this.render();
  }

  handleSlotRemove(event) {
    const slot = event.target.closest('[data-tile-key]');
    if (!slot) return;
    const key = slot.dataset.tileKey;
    const idx = this.state.items.findIndex((i) => i.tileKey === key);
    if (idx === -1) return;

    this.state.items[idx].quantity -= 1;
    if (this.state.items[idx].quantity <= 0) {
      this.state.items.splice(idx, 1);
    }
    this.#clearPresetAttribution();
    this.render();
  }

  async handleSubmit() {
    if (this.state.submitting) return;
    if (this.#totalQuantity() < this.state.minItems) return;

    this.state.submitting = true;
    this.refs.cta.setAttribute('disabled', '');

    const isSubscribe = this.state.purchaseType === 'subscribe';
    let items;

    if (isSubscribe) {
      // Subscription bundles use the Recharge Bundles 2.0 contract: one line per
      // component (with its selling plan), grouped by _rc_bundle*. Recharge
      // applies the subscription bundle discount at checkout.
      const bundleId = this.#generateBundleId();
      items = this.state.items
        .map((item) => {
          const plan = this.#planForItem(item);
          if (!plan) return null;
          return {
            id: parseInt(item.variantId, 10),
            quantity: item.quantity,
            selling_plan: plan.id,
            properties: {
              _rc_bundle: bundleId,
              _rc_bundle_parent: this.state.parentHandle,
              _rc_bundle_variant: this.state.parentVariantId,
              // Records which set the bundle started from (dropped on any core
              // edit → custom build). Underscore-prefixed: saved to the order,
              // hidden from the customer-facing cart.
              ...(this.state.presetTitle
                ? { _preset_title: this.state.presetTitle, _preset_handle: this.state.presetHandle }
                : {}),
            },
          };
        })
        .filter(Boolean);

      if (items.length !== this.state.items.length) {
        this.#showError('One or more selected products do not have a matching subscription frequency.');
        this.state.submitting = false;
        this.refs.cta.removeAttribute('disabled');
        return;
      }

      if (this.state.gift) {
        const gift = this.state.gift;
        items.push({
          id: parseInt(gift.variantId, 10),
          quantity: 1,
          properties: {
            _rc_bundle: bundleId,
            _rc_bundle_parent: this.state.parentHandle,
            _rc_bundle_variant: this.state.parentVariantId,
            _gift: 'true',
            _is_free: 'true',
          },
        });
      }

      for (const u of this.state.upsells) {
        items.push({
          id: parseInt(u.variantId, 10),
          quantity: u.quantity,
          properties: {
            _rc_bundle: bundleId,
            _rc_bundle_parent: this.state.parentHandle,
            _rc_bundle_variant: this.state.parentVariantId,
            _source: 'bundle-upsell',
          },
        });
      }
    } else {
      // One-time bundles use the FlexBundles contract: a SINGLE parent-variant
      // line carrying the chosen products. FlexBundles reads these properties,
      // explodes the bundle and prices it. The full contract (per FlexBundles'
      // sample cart/add.js payload) is THREE parts, and all are required for it
      // to recognise and process the line:
      //   `_components` - JSON array, one entry per component variant
      //   `Item N`      - one human-readable line per `_components` entry (1:1)
      //   `_settings`   - bundle title + image (image must be a Shopify CDN url)
      // Dropping `Item N` stops FlexBundles processing the line entirely (it stays
      // a plain parent variant at its base price - has_components:false).
      //
      // FlexBundles sums the per-entry `price` and applies that as the bundle's
      // price (it does NOT re-discount). Emit ONE entry per unit (quantity 1,
      // repeated for duplicates) at the DISCOUNTED per-unit price: with quantity
      // always 1 the sum is correct whether FlexBundles multiplies price*quantity
      // or not, and a `Item N` line pairs 1:1 with each entry. Aggregating into
      // quantity:N is what mispriced preset bundles (repeated products).
      const factor = (100 - this.#discountPercent()) / 100;
      const components = [];
      const itemLabels = [];
      for (const item of this.state.items) {
        const price = Math.round((item.variantPrice || 0) * factor) / 100;
        for (let n = 0; n < item.quantity; n++) {
          components.push({ id: parseInt(item.variantId, 10), quantity: 1, price });
          itemLabels.push(item.productTitle);
        }
      }

      const properties = { _components: JSON.stringify(components) };
      itemLabels.forEach((label, i) => {
        properties[`Item ${i + 1}`] = label;
      });
      // `_settings.image` must be an absolute https URL on the Shopify CDN
      // (per the FlexBundles sample payload). Liquid's image_url emits
      // protocol-relative, often custom-domain, URLs - and a bad image doesn't
      // just drop the thumbnail: it invalidates the whole cart transform,
      // knocking EVERY bundle in the cart back to the parent's base price.
      // Normalize to https and only pass cdn.shopify.com URLs; otherwise omit
      // (image is optional).
      const settingsImage = this.#cdnImageUrl(this.state.presetImage);
      properties._settings = JSON.stringify({
        title: this.state.presetTitle || this.dataset.parentTitle || 'Bundle',
        ...(settingsImage ? { image: settingsImage } : {}),
      });
      // Every one-time bundle rides the SAME parent variant, so two adds whose
      // properties happen to match (the classic case: the same preset added
      // twice, or a build whose items match an existing line) would be merged by
      // Shopify into a single line - the earlier bundle looks "overwritten".
      // FlexBundles has no bundle-instance id of its own, so stamp a unique one
      // (underscore-prefixed: saved to the order, hidden from the cart) to keep
      // every add a distinct line. Mirrors the subscribe path's _rc_bundle.
      properties._bundle_id = this.#generateBundleId();

      items = [{ id: parseInt(this.state.parentVariantId, 10), quantity: 1, properties }];

      // One-time upsell add-ons ride along as separate, full-price lines.
      for (const u of this.state.upsells) {
        items.push({ id: parseInt(u.variantId, 10), quantity: u.quantity });
      }
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    const body = JSON.stringify({ items, sections: sectionIds.join(',') });

    try {
      const url = window.Theme?.routes?.cart_add_url || '/cart/add.js';
      const response = await fetch(url, fetchConfig('json', { body }));
      const data = await response.json();

      if (!response.ok || data.status) {
        this.#showError(data.description || data.message || 'Could not add bundle to cart.');
        return;
      }

      this.#track('bundle_add_to_cart', {
        parent: this.state.parentHandle,
        preset: this.state.presetTitle || null,
        purchaseType: this.state.purchaseType,
        frequency: isSubscribe ? this.state.frequencyName : null,
        size: this.#totalQuantity(),
        value: this.#currentSubtotal() / 100,
        discountPercent: this.#discountPercent(),
        items: this.state.items.map((i) => ({ variantId: i.variantId, title: i.productTitle, quantity: i.quantity })),
        upsells: this.state.upsells.map((u) => ({ variantId: u.variantId, title: u.productTitle, quantity: u.quantity })),
      });

      this.state.items = [];
      this.state.upsells = [];
      this.#clearPresetAttribution();
      this.#hideError();
      this.render();

      this.dispatchEvent(
        new CartUpdateEvent({}, this.id || 'subscription-bundle-builder', {
          source: 'subscription-bundle-builder',
          sections: data.sections,
          itemCount: items.length,
        })
      );

      if (this.state.ctaAction === 'checkout') {
        window.location.href = window.Theme?.routes?.checkout_url || '/checkout';
        return;
      }

      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    } catch (err) {
      console.error(err);
      this.#showError('Could not add bundle to cart. Please try again.');
    } finally {
      this.state.submitting = false;
      this.refs.cta.removeAttribute('disabled');
    }
  }

  // -- render -------------------------------------------------------------

  render() {
    this.#renderTiles();
    this.#renderSlots();
    this.#renderUpsells();
    this.#renderUpsellSlots();
    this.#renderProgressBar();
    this.#renderTierMessage();
    this.#renderSavingsSticker();
    this.#maybeCelebrateUnlock();
    this.#renderPresetLabel();
    this.#renderPresetPicker();
    this.#renderShipping();
    this.#renderTotals();
    this.#renderCta();
    this.#renderProgress();
    this.#persist();
  }

  #renderShipping() {
    const bar = this.refs.shippingBar;
    if (!bar) return;

    const threshold = this.state.freeShippingThreshold;
    if (threshold <= 0 || this.#totalQuantity() === 0) {
      bar.setAttribute('hidden', '');
      return;
    }
    bar.removeAttribute('hidden');

    const subtotal = this.#currentSubtotal();
    const remaining = Math.max(0, threshold - subtotal);
    const pct = Math.max(0, Math.min(100, (subtotal / threshold) * 100));
    const unlocked = remaining <= 0;

    bar.dataset.unlocked = unlocked ? 'true' : 'false';
    if (this.refs.shippingFill) this.refs.shippingFill.style.width = `${pct}%`;
    const msgTarget = this.refs.shippingMsgText || this.refs.shippingMsg;
    if (msgTarget) {
      msgTarget.textContent = unlocked
        ? this.freeShippingUnlockedLabel
        : this.freeShippingProgressLabel.replace('[amount]', this.#formatMoney(remaining));
    }
  }

  #renderUpsellSlots() {
    const container = this.refs.upsellSlots;
    if (!container) return;

    const flat = [];
    for (const u of this.state.upsells) {
      for (let q = 0; q < u.quantity; q++) flat.push(u);
    }

    if (flat.length === 0) {
      container.setAttribute('hidden', '');
    } else {
      container.removeAttribute('hidden');
    }

    const slotCount = parseInt(container.dataset.slotCount, 10) || 6;
    const max = this.#upsellMax();
    const cap = Number.isFinite(max) ? max : slotCount;
    const totalSlots = Math.min(cap, Math.max(slotCount, flat.length));
    container.style.setProperty('--upsell-slot-count', String(totalSlots));

    container.innerHTML = '';
    for (let i = 0; i < totalSlots; i++) {
      const item = flat[i];
      const wrap = document.createElement('li');
      wrap.className = 'bundle-builder__upsell-slot-wrap';
      if (item) {
        const priceText = this.#formatMoney(item.price);
        wrap.innerHTML = `
          <div class="bundle-builder__upsell-slot" data-filled="true" data-upsell-key="${escapeHtml(item.tileKey)}" style="background-image: url(${escapeHtml(item.image || '')})">
            <button type="button" class="bundle-builder__upsell-slot-remove" aria-label="Remove ${escapeHtml(item.productTitle)}" on:click="/handleUpsellSlotRemove">×</button>
          </div>
          <span class="bundle-builder__upsell-slot-title">${escapeHtml(item.productTitle)} - ${escapeHtml(priceText)}</span>
        `;
      } else {
        wrap.innerHTML = `
          <div class="bundle-builder__upsell-slot" aria-label="Empty extra slot ${i + 1}"></div>
          <span class="bundle-builder__upsell-slot-title"></span>
        `;
      }
      container.appendChild(wrap);
    }
  }

  #renderUpsells() {
    const cards = this.querySelectorAll('.bundle-builder__upsell-card');
    const byKey = new Map(this.state.upsells.map((u) => [u.tileKey, u]));
    const atMax = this.#totalUpsellQuantity() >= this.#upsellMax();

    cards.forEach((card) => {
      const addBtn = card.querySelector('.bundle-builder__upsell-add');
      const qty = card.querySelector('.bundle-builder__upsell-qty');
      if (!addBtn || !qty) return;

      const item = byKey.get(card.dataset.upsellKey);
      if (item) {
        addBtn.setAttribute('hidden', '');
        qty.removeAttribute('hidden');
        qty.querySelector('.bundle-builder__upsell-qty-num').textContent = String(item.quantity);
        const incBtn = qty.querySelector('[on\\:click="/handleUpsellQtyIncrement"]');
        if (incBtn) {
          if (atMax) incBtn.setAttribute('disabled', '');
          else incBtn.removeAttribute('disabled');
        }
      } else {
        qty.setAttribute('hidden', '');
        addBtn.removeAttribute('hidden');
        if (atMax) addBtn.setAttribute('disabled', '');
        else addBtn.removeAttribute('disabled');
      }
    });
  }

  #totalUpsellQuantity() {
    return this.state.upsells.reduce((acc, u) => acc + u.quantity, 0);
  }

  #upsellMax() {
    const section = this.querySelector('.bundle-builder__upsells');
    if (!section) return Infinity;
    const max = parseInt(section.dataset.maxUpsells, 10);
    return Number.isFinite(max) && max > 0 ? max : Infinity;
  }

  #renderTiles() {
    const itemsByKey = new Map(this.state.items.map((i) => [i.tileKey, i]));
    const atMax = this.#totalQuantity() >= this.state.maxItems;

    this.querySelectorAll('.bundle-builder__tile').forEach((tile) => {
      if (tile.dataset.disabled === 'true') return;
      const addBtn = tile.querySelector('.bundle-builder__tile-add');
      const qty = tile.querySelector('.bundle-builder__qty');
      if (!addBtn || !qty) return;

      const item = itemsByKey.get(tile.dataset.productId);

      if (item) {
        addBtn.setAttribute('hidden', '');
        qty.removeAttribute('hidden');
        qty.querySelector('.bundle-builder__qty-num').textContent = String(item.quantity);
        const incBtn = qty.querySelector('[on\\:click="/handleQtyIncrement"]');
        if (incBtn) {
          if (atMax) incBtn.setAttribute('disabled', '');
          else incBtn.removeAttribute('disabled');
        }
      } else {
        qty.setAttribute('hidden', '');
        addBtn.removeAttribute('hidden');
        if (atMax) addBtn.setAttribute('disabled', '');
        else addBtn.removeAttribute('disabled');
      }
    });
  }

  #renderSlots() {
    const container = this.refs.slots;
    const totalQty = this.#totalQuantity();
    // Show the required minimum first; once it's met, reveal the remaining
    // optional slots (up to the max) as blanks so the shopper knows they can
    // keep adding.
    const base = totalQty >= this.state.minItems ? this.state.maxItems : this.state.minItems;
    const slotCount = Math.min(this.state.maxItems, Math.max(base, totalQty));

    const flat = [];
    for (const item of this.state.items) {
      for (let q = 0; q < item.quantity; q++) flat.push(item);
    }

    container.innerHTML = '';

    for (let i = 0; i < slotCount; i++) {
      const item = flat[i];
      const wrap = document.createElement('li');
      wrap.className = 'bundle-builder__slot-wrap';
      if (item) {
        wrap.innerHTML = `
          <div class="bundle-builder__slot" data-filled="true" data-tile-key="${escapeHtml(item.tileKey)}" style="background-image: url(${escapeHtml(item.image || '')})">
            <button type="button" class="bundle-builder__slot-remove" aria-label="Remove ${escapeHtml(item.productTitle)}" on:click="/handleSlotRemove">×</button>
          </div>
          <span class="bundle-builder__slot-title">${escapeHtml(item.productTitle)}</span>
        `;
      } else {
        wrap.innerHTML = `
          <div class="bundle-builder__slot" aria-label="Empty slot ${i + 1}">
            <span class="bundle-builder__slot-plus" aria-hidden="true">+</span>
          </div>
          <span class="bundle-builder__slot-title"></span>
        `;
      }
      container.appendChild(wrap);
    }

    if (this.refs.trayToggleCount) {
      this.refs.trayToggleCount.textContent = String(totalQty);
    }
    if (this.refs.trayToggleGoal) {
      this.refs.trayToggleGoal.textContent = String(this.#goalCount());
    }
  }

  #renderProgressBar() {
    const bar = this.refs.progressBar;
    if (!bar) return;
    const count = this.#totalQuantity();
    const tiersActive = this.#tiersActive();
    const goal = this.#goalCount() || 1;
    const pct = Math.max(0, Math.min(100, (count / goal) * 100));
    const shown = Math.min(count, goal);
    bar.style.setProperty('--progress', `${pct}%`);

    if (this.refs.progressBarCount) this.refs.progressBarCount.textContent = String(shown);
    if (this.refs.progressGoal) this.refs.progressGoal.textContent = String(goal);
    if (this.refs.progressLabelCount) this.refs.progressLabelCount.textContent = String(shown);
    if (this.refs.progressLabelGoal) this.refs.progressLabelGoal.textContent = String(goal);

    // Tiered one-time bar: segment the track into one notch per item and drop a
    // milestone dot at each interior tier (the top tier is the goal circle).
    bar.dataset.segmented = tiersActive ? 'true' : 'false';
    if (tiersActive) bar.style.setProperty('--bb-segments', String(goal));

    if (this.refs.progressTiers) {
      if (tiersActive) {
        const max = this.state.maxItems;
        this.refs.progressTiers.innerHTML = this.state.discountTiers
          .filter((t) => t.qty < max)
          .map((t) => {
            const posPct = Math.min(100, (t.qty / max) * 100);
            const reached = count >= t.qty;
            return `<span class="bundle-builder__tier-mark" style="left:${posPct}%" data-reached="${reached}"></span>`;
          })
          .join('');
      } else {
        this.refs.progressTiers.innerHTML = '';
      }
    }

    const marker = this.refs.progressMarker;
    if (marker) marker.removeAttribute('hidden');
  }

  // "Add N more to save X%" while a higher tier is reachable, then "You're saving X%"
  // at the top tier. Hidden when there are no tiers or in subscription mode.
  #renderTierMessage() {
    const el = this.refs.tierMsg;
    if (!el) return;

    if (!this.state.discountTiers.length || this.state.purchaseType === 'subscribe') {
      el.setAttribute('hidden', '');
      return;
    }

    const next = this.#nextTier();
    let msg = '';
    if (next) {
      const need = Math.max(0, next.qty - this.#totalQuantity());
      msg = this.tierProgressLabel
        .replace('[count]', String(need))
        .replace('[percent]', String(next.percent));
    } else {
      const pct = this.#discountPercent();
      if (pct > 0) msg = this.tierUnlockedLabel.replace('[percent]', String(pct));
    }

    if (msg) {
      el.textContent = msg;
      el.dataset.unlocked = next ? 'false' : 'true';
      el.removeAttribute('hidden');
    } else {
      el.setAttribute('hidden', '');
    }
  }

  // Sticker shows the discount currently applied (the highest qualifying tier for
  // the live quantity), updating as items change. Hidden in subscription mode and
  // until the first tier is reached, since no discount applies below it.
  #renderSavingsSticker() {
    const el = this.refs.savingsSticker;
    if (!el) return;
    const pct = this.#discountPercent();
    if (this.#tiersActive() && pct > 0) {
      const percentEl = el.querySelector('.bundle-builder__savings-sticker-percent');
      if (percentEl) percentEl.textContent = `${pct}%`;
      el.removeAttribute('hidden');
    } else {
      el.setAttribute('hidden', '');
    }
  }

  // The highest tier the current quantity qualifies for (one-time only), or null.
  #highestReachedTier() {
    if (!this.#tiersActive()) return null;
    const qty = this.#totalQuantity();
    let best = null;
    for (const t of this.state.discountTiers) {
      if (qty >= t.qty && (!best || t.qty > best.qty)) best = t;
    }
    return best;
  }

  // Fire a one-shot celebration when the shopper crosses into a new tier. The first
  // render (load / restore / preset / mode toggle) only baselines — it never bursts.
  #maybeCelebrateUnlock() {
    const reached = this.#highestReachedTier();
    const reachedQty = reached ? reached.qty : 0;

    if (this.lastReachedTierQty === undefined) {
      this.lastReachedTierQty = reachedQty;
      return;
    }
    if (reachedQty <= this.lastReachedTierQty) {
      this.lastReachedTierQty = reachedQty;
      return;
    }
    this.lastReachedTierQty = reachedQty;
    if (!reached) return;

    // Celebratory copy in the tier message, reverted on the next render.
    const el = this.refs.tierMsg;
    if (el) {
      el.textContent = this.tierUnlockLabel.replace('[percent]', String(reached.percent));
      el.dataset.unlocked = 'true';
      el.removeAttribute('hidden');
      clearTimeout(this.unlockTimer);
      this.unlockTimer = setTimeout(() => this.render(), 1600);
    }
  }

  #renderTotals() {
    const isSubscribe = this.state.purchaseType === 'subscribe';
    let bundleSubtotal = 0;
    let baseline = 0;

    for (const item of this.state.items) {
      const plan = isSubscribe ? this.#planForItem(item) : null;
      const perUnit = plan ? plan.perDeliveryPrice : item.variantPrice;
      bundleSubtotal += perUnit * item.quantity;
      baseline += item.variantPrice * item.quantity;
    }

    // Subscription savings come from the selling plan's per-delivery price.
    let savings = Math.max(0, baseline - bundleSubtotal);

    // One-time bundles get a tiered Flex Bundles discount based on item count.
    const bundleDiscount = this.#bundleDiscount(bundleSubtotal);
    bundleSubtotal = Math.max(0, bundleSubtotal - bundleDiscount);
    savings += bundleDiscount;

    // Upsells contribute to subtotal at full price but never to savings.
    let subtotal = bundleSubtotal;
    for (const u of this.state.upsells) {
      subtotal += u.price * u.quantity;
    }

    this.refs.subtotal.textContent = this.#formatMoney(subtotal);

    // Stash for #renderCta (runs right after) so the Add button can show the
    // same struck-through pre-discount price.
    this.ctaSavings = savings;

    // Strike through the pre-discount price (subtotal + savings) when anything was saved.
    if (this.refs.subtotalCompare) {
      if (savings > 0) {
        this.refs.subtotalCompare.textContent = this.#formatMoney(subtotal + savings);
        this.refs.subtotalCompare.removeAttribute('hidden');
      } else {
        this.refs.subtotalCompare.setAttribute('hidden', '');
      }
    }

    if (savings > 0) {
      this.refs.savings.textContent = this.#formatMoney(savings);
      this.refs.savingsRow.removeAttribute('hidden');
    } else {
      this.refs.savingsRow.setAttribute('hidden', '');
    }
  }

  #renderCta() {
    const count = this.#totalQuantity();
    const need = Math.max(0, this.state.minItems - count);
    const cta = this.refs.cta;
    const label = this.refs.ctaLabel;
    const isSubscribe = this.state.purchaseType === 'subscribe';

    if (need > 0) {
      // Below minimum: disabled everywhere; CSS hides it on mobile only (space
      // saving) while desktop shows the disabled state. The progress label carries
      // the "N more" cue when it's hidden.
      cta.dataset.belowMin = 'true';
      cta.setAttribute('disabled', '');
      label.textContent = `Add ${need} more to ${isSubscribe ? 'subscribe' : 'continue'}`;
    } else {
      cta.dataset.belowMin = 'false';
      cta.removeAttribute('disabled');
      const subtotal = this.#currentSubtotal();
      const total = this.#formatMoney(subtotal);
      const savings = this.ctaSavings || 0;
      let actionLabel;
      if (this.state.ctaAction === 'checkout') actionLabel = 'Continue to checkout';
      else actionLabel = isSubscribe ? 'Subscribe' : 'Add';
      // Show the struck-through pre-discount price so the savings is visible,
      // matching the cross-out used on the cards / cart.
      if (savings > 0) {
        const compare = this.#formatMoney(subtotal + savings);
        label.innerHTML = `${actionLabel} - <s class="bundle-builder__cta-compare">${compare}</s> ${total}`;
      } else {
        label.textContent = `${actionLabel} - ${total}`;
      }
    }

    if (this.refs.trayToggleCta) {
      if (need > 0) {
        this.refs.trayToggleCta.setAttribute('hidden', '');
      } else {
        this.refs.trayToggleCta.removeAttribute('hidden');
      }
    }

    if (this.refs.tray) {
      this.refs.tray.dataset.complete = need > 0 ? 'false' : 'true';
    }
  }

  #renderProgress() {
    if (this.refs.progressCount) {
      this.refs.progressCount.textContent = String(this.#totalQuantity());
    }
  }

  // Show the originating set's name while the bundle is still the pristine preset.
  // Cleared (hidden) the moment the shopper edits → it reads as a custom build.
  #renderPresetLabel() {
    const active = !!this.state.presetTitle;

    // The preset preview banner (name + thumbnail + description) is only shown
    // when a set was deep-linked via ?preset=<handle>; hidden for normal builds.
    const head = this.refs.progressHead;
    if (head) head.dataset.preset = active ? 'true' : 'false';

    // The bundle heading becomes the preset title while pristine, then reverts to
    // its default ("Your bundle") on edit. The thumbnail + description ride along.
    const nameEl = this.refs.bundleName;
    if (nameEl) {
      nameEl.textContent = active ? this.state.presetTitle : nameEl.dataset.defaultLabel || '';
    }

    const desc = this.refs.presetDescText;
    if (desc) {
      if (active && this.state.presetDescription) {
        desc.textContent = this.state.presetDescription;
        desc.removeAttribute('hidden');
      } else {
        desc.textContent = '';
        desc.setAttribute('hidden', '');
      }
    }

    const img = this.refs.presetImage;
    if (img) {
      if (active && this.state.presetImage) {
        img.src = this.state.presetImage;
        img.removeAttribute('hidden');
      } else {
        img.removeAttribute('src');
        img.setAttribute('hidden', '');
      }
    }
  }

  // -- helpers ------------------------------------------------------------

  #totalQuantity() {
    return this.state.items.reduce((acc, i) => acc + i.quantity, 0);
  }

  // The denominator shown in the tray ("count / goal") and the progress bar target.
  // Stays at the minimum until the bundle is complete, then grows with the count
  // (so 9 items reads "9/9") up to the maximum.
  #tiersActive() {
    return this.state.purchaseType !== 'subscribe' && this.state.discountTiers.length > 0;
  }

  #goalCount() {
    const count = this.#totalQuantity();
    // With tiered discounts the bar is a fixed 0→max scale (notches + tier dots mark
    // the milestones) so the fill only ever moves forward. Without tiers it falls
    // back to the minimum, growing with the count up to the maximum.
    if (this.#tiersActive()) return this.state.maxItems;
    return Math.min(this.state.maxItems, Math.max(this.state.minItems, count));
  }

  #expandTray() {
    const tray = this.refs.tray;
    if (!tray || tray.dataset.state === 'expanded') return;
    tray.dataset.state = 'expanded';
    this.refs.trayToggle?.setAttribute('aria-expanded', 'true');
  }

  #scrollLatestSlotIntoView() {
    const container = this.refs.slots;
    if (!container) return;
    const filled = container.querySelectorAll('.bundle-builder__slot[data-filled="true"]');
    const latest = filled[filled.length - 1];
    if (!latest) return;
    const wrap = latest.closest('.bundle-builder__slot-wrap') || latest;
    // Scroll only the horizontal slot rail - never the page or the tray - so
    // adding an item can't move or appear to expand the tray. (scrollIntoView
    // would scroll ancestor/scroll containers, including the viewport.)
    if (container.scrollWidth <= container.clientWidth) return;
    const cRect = container.getBoundingClientRect();
    const wRect = wrap.getBoundingClientRect();
    const delta = wRect.left + wRect.width / 2 - (cRect.left + cRect.width / 2);
    container.scrollBy({ left: delta, behavior: 'smooth' });
  }

  #planForItem(item) {
    if (!item.sellingPlans || item.sellingPlans.length === 0) return null;
    const byName = item.sellingPlans.find((p) => p.name === this.state.frequencyName);
    return byName || item.sellingPlans[0];
  }

  #currentSubtotal() {
    const isSubscribe = this.state.purchaseType === 'subscribe';
    let bundleTotal = this.state.items.reduce((acc, item) => {
      const plan = isSubscribe ? this.#planForItem(item) : null;
      const per = plan ? plan.perDeliveryPrice : item.variantPrice;
      return acc + per * item.quantity;
    }, 0);
    bundleTotal = Math.max(0, bundleTotal - this.#bundleDiscount(bundleTotal));
    const upsellTotal = this.state.upsells.reduce((acc, u) => acc + u.price * u.quantity, 0);
    return bundleTotal + upsellTotal;
  }

  // Highest qualifying discount rate (percent) for the current quantity. Tiers are
  // one-time only — subscription savings come from the selling plan's per-delivery price.
  #discountPercent() {
    if (this.state.purchaseType === 'subscribe') return 0;
    const qty = this.#totalQuantity();
    let pct = 0;
    for (const tier of this.state.discountTiers) {
      if (qty >= tier.qty && tier.percent > pct) pct = tier.percent;
    }
    return pct;
  }

  // The next unreached tier — used for the "Add N more to save X%" nudge. Null at
  // the top tier (or when no tiers / subscription mode).
  #nextTier() {
    if (this.state.purchaseType === 'subscribe') return null;
    const qty = this.#totalQuantity();
    for (const tier of this.state.discountTiers) {
      if (tier.qty > qty) return tier;
    }
    return null;
  }

  // Discount in cents applied to a given pre-discount bundle subtotal.
  #bundleDiscount(bundleSubtotal) {
    const pct = this.#discountPercent();
    if (pct <= 0) return 0;
    return Math.round((bundleSubtotal * pct) / 100);
  }

  // -- presets ------------------------------------------------------------

  // Read the ?preset=<handle> deep link and apply the matching set, if any.
  // Returns true when a non-empty selection was applied.
  #applyPresetFromUrl() {
    let handle;
    try {
      handle = new URLSearchParams(window.location.search).get('preset');
    } catch (_) {
      return false;
    }
    if (!handle) return false;
    const preset = this.#presetMap()[handle];
    if (!preset || !Array.isArray(preset.items) || preset.items.length === 0) return false;
    const applied = this.#applyPreset(preset.items);
    if (applied) {
      this.state.presetHandle = handle;
      this.state.presetTitle = preset.title || '';
      this.state.presetImage = preset.image || '';
      this.state.presetDescription = preset.description || '';
    }
    return applied;
  }

  // Jump-start picker: clicking a set tile applies it in place (no reload) and
  // marks that tile active. Mirrors the ?preset deep link, minus the navigation.
  handlePresetSelect(event) {
    const tile = event.target.closest('[data-preset-handle]');
    if (!tile) return;
    const handle = tile.dataset.presetHandle;
    const preset = this.#presetMap()[handle];
    if (!preset || !Array.isArray(preset.items) || preset.items.length === 0) return;
    const applied = this.#applyPreset(preset.items);
    if (applied) {
      this.state.presetHandle = handle;
      this.state.presetTitle = preset.title || '';
      this.state.presetImage = preset.image || '';
      this.state.presetDescription = preset.description || '';
      // Keep the URL in the same state a PDP set tile would have landed on
      // (?preset=<handle>), so the address bar is shareable and a refresh
      // re-applies the same set instead of falling back to a saved draft.
      this.#setPresetParam(handle);
    }
    this.render();
    this.#scrollLatestSlotIntoView();
  }

  // Highlight the set tile matching the active preset (cleared when edited).
  #renderPresetPicker() {
    const picker = this.refs.presetPicker;
    if (!picker) return;
    const active = this.state.presetHandle;
    picker.querySelectorAll('[data-preset-handle]').forEach((tile) => {
      const on = !!active && tile.dataset.presetHandle === active;
      tile.classList.toggle('is-active', on);
      // The tile is the card; the pressed state belongs to its "Add set" button.
      tile.querySelector('.bundle-builder__preset-add')?.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  // The bundle is no longer "the preset" once its core items are edited — drop the
  // title/handle, strip the ?preset param (so a refresh keeps the edited draft
  // instead of re-applying the set), and let it persist as a custom (BYOB) build.
  #clearPresetAttribution() {
    if (!this.state.presetHandle && !this.state.presetTitle) return;
    this.state.presetTitle = '';
    this.state.presetHandle = '';
    this.state.presetImage = '';
    this.state.presetDescription = '';
    this.#clearPresetParam();
  }

  // Server-rendered { presetHandle: { title, items: [productId, ...] } } map.
  // Cached after first read.
  #presetMap() {
    if (this._presetMap) return this._presetMap;
    let map = {};
    try {
      map = JSON.parse(this.querySelector('script[data-bundle-presets]')?.textContent || '{}');
    } catch (_) {
      /* malformed map — fall through to empty */
    }
    this._presetMap = map && typeof map === 'object' ? map : {};
    return this._presetMap;
  }

  // Replace the current selection with a preset's products, hydrating each from
  // its rendered tile (same contract as #restore — prices/plans come from the DOM,
  // never the preset map). Repeated ids accumulate quantity. Respects maxItems.
  #applyPreset(productIds) {
    const tileByKey = new Map();
    this.querySelectorAll('.bundle-builder__tile').forEach((t) => tileByKey.set(t.dataset.productId, t));

    const items = [];
    let total = 0;
    for (const id of productIds) {
      if (total >= this.state.maxItems) break;
      const tile = tileByKey.get(String(id));
      if (!tile || tile.dataset.disabled === 'true') continue;

      const existing = items.find((i) => i.tileKey === tile.dataset.productId);
      if (existing) {
        existing.quantity += 1;
        total += 1;
        continue;
      }

      let plans = [];
      try {
        plans = JSON.parse(tile.dataset.sellingPlans || '[]');
      } catch (_) {
        continue;
      }
      if (this.state.requirePlans && plans.length === 0) continue;

      items.push({
        tileKey: tile.dataset.productId,
        productHandle: tile.dataset.productHandle,
        productTitle: tile.dataset.productTitle,
        variantId: tile.dataset.variantId,
        variantPrice: parseInt(tile.dataset.variantPrice, 10) || 0,
        image: tile.dataset.image,
        sellingPlans: plans,
        quantity: 1,
      });
      total += 1;
    }

    if (items.length === 0) return false;
    this.state.items = items;
    return true;
  }

  // Modal-only: seed the bundle with the first `minItems` AVAILABLE tiles in DOM
  // order (panels render first-collection-first, falling through to later panels).
  // Reuses #applyPreset so hydration / price / plan rules are identical to a
  // preset apply. Distinct ids → one qty-1 item each.
  #applyAutoDefault() {
    const ids = [];
    const tiles = this.querySelectorAll('.bundle-builder__panel .bundle-builder__tile');
    for (const tile of tiles) {
      if (ids.length >= this.state.minItems) break;
      if (tile.dataset.disabled === 'true') continue;
      ids.push(tile.dataset.productId);
    }
    if (ids.length) this.#applyPreset(ids);
  }

  // Write ?preset=<handle> onto the URL when a set is applied in place, so an
  // on-page pick ends up at the same URL as a PDP set tile's deep link. Skipped
  // in the Quick Add modal — the host page's URL isn't the builder's.
  #setPresetParam(handle) {
    if (this.modalContext || !handle) return;
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('preset') === handle) return;
      url.searchParams.set('preset', handle);
      window.history.replaceState({}, '', url);
    } catch (_) {
      /* history unavailable — non-fatal */
    }
  }

  // Drop ?preset from the URL after applying so a later edit + refresh isn't
  // clobbered back to the preset (the edited draft persists via localStorage).
  #clearPresetParam() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('preset');
      window.history.replaceState({}, '', url);
    } catch (_) {
      /* history unavailable — non-fatal */
    }
  }

  // -- persistence --------------------------------------------------------

  #storageKey() {
    return `bundle-builder:${this.state.parentHandle || this.id || 'default'}`;
  }

  // Persist only keys + quantities; prices/plans are re-hydrated from the DOM on
  // restore so a stale snapshot can never reintroduce an old price.
  #persist() {
    // The modal instance is ephemeral: never write — or remove — the PDP's draft
    // (both share the parentHandle storage key). No #restore runs in the modal
    // either, so the PDP draft is fully isolated from Quick Add edits.
    if (this.modalContext) return;

    let store;
    try {
      store = window.localStorage;
    } catch (_) {
      return;
    }
    if (!store) return;
    try {
      const key = this.#storageKey();
      // A pristine preset is ephemeral and URL-driven — never persist it (and clear
      // any stale draft), so it only ever appears when ?preset is on the URL. Once
      // edited, #clearPresetAttribution unsets presetHandle and it persists normally.
      if (this.state.presetHandle) {
        store.removeItem(key);
        return;
      }
      if (this.state.items.length === 0 && this.state.upsells.length === 0) {
        store.removeItem(key);
        return;
      }
      store.setItem(
        key,
        JSON.stringify({
          v: 1,
          purchaseType: this.state.purchaseType,
          frequencyName: this.state.frequencyName,
          items: this.state.items.map((i) => ({ key: i.tileKey, quantity: i.quantity })),
          upsells: this.state.upsells.map((u) => ({ key: u.tileKey, quantity: u.quantity })),
        })
      );
    } catch (_) {
      /* quota / private mode — non-fatal */
    }
  }

  #restore() {
    let stored;
    try {
      const raw = window.localStorage?.getItem(this.#storageKey());
      if (!raw) return;
      stored = JSON.parse(raw);
    } catch (_) {
      return;
    }
    if (!stored || typeof stored !== 'object') return;

    const tileByKey = new Map();
    this.querySelectorAll('.bundle-builder__tile').forEach((t) => tileByKey.set(t.dataset.productId, t));

    let total = 0;
    const items = [];
    for (const entry of Array.isArray(stored.items) ? stored.items : []) {
      const tile = tileByKey.get(entry.key);
      if (!tile || tile.dataset.disabled === 'true') continue;
      let plans = [];
      try {
        plans = JSON.parse(tile.dataset.sellingPlans || '[]');
      } catch (_) {
        continue;
      }
      if (this.state.requirePlans && plans.length === 0) continue;
      const qty = Math.min(Math.max(1, entry.quantity | 0), this.state.maxItems - total);
      if (qty <= 0) break;
      items.push({
        tileKey: tile.dataset.productId,
        productHandle: tile.dataset.productHandle,
        productTitle: tile.dataset.productTitle,
        variantId: tile.dataset.variantId,
        variantPrice: parseInt(tile.dataset.variantPrice, 10) || 0,
        image: tile.dataset.image,
        sellingPlans: plans,
        quantity: qty,
      });
      total += qty;
    }
    this.state.items = items;

    const cardByKey = new Map();
    this.querySelectorAll('.bundle-builder__upsell-card').forEach((c) => cardByKey.set(c.dataset.upsellKey, c));
    const upsells = [];
    let upsellTotal = 0;
    const upsellMax = this.#upsellMax();
    for (const entry of Array.isArray(stored.upsells) ? stored.upsells : []) {
      const card = cardByKey.get(entry.key);
      if (!card) continue;
      const qty = Math.min(Math.max(1, entry.quantity | 0), upsellMax - upsellTotal);
      if (qty <= 0) break;
      upsells.push({
        tileKey: card.dataset.upsellKey,
        variantId: card.dataset.variantId,
        productTitle: card.dataset.productTitle,
        price: parseInt(card.dataset.variantPrice, 10) || 0,
        image: card.dataset.image,
        quantity: qty,
      });
      upsellTotal += qty;
    }
    this.state.upsells = upsells;

    // Restore purchase type / frequency only when matching controls exist.
    if (stored.purchaseType && this.querySelector('.bundle-builder__purchase-toggle')) {
      this.state.purchaseType = stored.purchaseType;
    }
    if (stored.frequencyName) {
      const hasFreq = Array.from(this.querySelectorAll('.bundle-builder__frequency-option input')).some(
        (input) => input.value === stored.frequencyName
      );
      if (hasFreq) this.state.frequencyName = stored.frequencyName;
    }
    this.#syncControls();
  }

  // Reflect restored purchaseType / frequency back into the radio UI.
  #syncControls() {
    this.querySelectorAll('.bundle-builder__purchase-option').forEach((opt) => {
      const input = opt.querySelector('input');
      const match = input?.value === this.state.purchaseType;
      if (input) input.checked = match;
      opt.classList.toggle('bundle-builder__purchase-option--active', match);
    });

    const isSubscribe = this.state.purchaseType === 'subscribe';
    if (this.refs.frequencyFieldset) this.refs.frequencyFieldset.toggleAttribute('hidden', !isSubscribe);
    if (this.refs.giftTrayCard) this.refs.giftTrayCard.toggleAttribute('hidden', !(isSubscribe && this.state.gift));

    this.querySelectorAll('.bundle-builder__frequency-option').forEach((opt) => {
      const input = opt.querySelector('input');
      const match = !!input && input.value === this.state.frequencyName;
      if (input) input.checked = match;
      opt.classList.toggle('bundle-builder__frequency-option--active', match);
    });
  }

  // -- analytics ----------------------------------------------------------

  // Push to the GTM/GA dataLayer and dispatch a DOM CustomEvent so either a tag
  // manager or a custom listener can measure bundle behaviour.
  #track(eventName, detail) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, bundle: detail });
    } catch (_) {
      /* no-op */
    }
    try {
      this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail }));
    } catch (_) {
      /* no-op */
    }
  }

  #generateBundleId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `bb-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
  }

  // FlexBundles' `_settings.image` contract: absolute https, hosted on the
  // Shopify CDN. Returns the normalized URL, or null when the source doesn't
  // qualify (better no thumbnail than a rejected cart transform).
  #cdnImageUrl(src) {
    if (!src) return null;
    try {
      const url = new URL(src.startsWith('//') ? `https:${src}` : src, window.location.origin);
      url.protocol = 'https:';
      return url.hostname === 'cdn.shopify.com' ? url.href : null;
    } catch (_) {
      return null;
    }
  }

  #formatMoney(cents) {
    const format = window.Shopify?.money_format || '${{amount}}';
    const currency = window.Shopify?.currency?.active || 'USD';
    const isWhole = cents % 100 === 0;
    const finalFormat = isWhole
      ? format
          .replace(/{{\s*amount\s*}}/g, '{{amount_no_decimals}}')
          .replace(
            /{{\s*amount_with_comma_separator\s*}}/g,
            '{{amount_no_decimals_with_comma_separator}}'
          )
      : format;
    try {
      return formatMoney(cents, finalFormat, currency);
    } catch (_) {
      return `$${(cents / 100).toFixed(isWhole ? 0 : 2)}`;
    }
  }

  #showError(msg) {
    if (!this.refs.error) return;
    this.refs.error.textContent = msg;
    this.refs.error.removeAttribute('hidden');
  }

  #hideError() {
    if (!this.refs.error) return;
    this.refs.error.setAttribute('hidden', '');
    this.refs.error.textContent = '';
  }
}

function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

if (!customElements.get('subscription-bundle-builder')) {
  customElements.define('subscription-bundle-builder', SubscriptionBundleBuilder);
}
